import ProjectApi from '~/common/api/zeiterfassungServerApi/ProjectApi';
import { useGetChecklistPdf } from '~/common/api/pdfServiceApi/PdfApi';
import { Job, Project } from '~/common/entities/Project/ProjectTypes';
import { JobApi } from '~/common/api/zeiterfassungServerApi/JobApi';
import routes from '~/common/constants/routes.json';
import { ID } from '~/common/api/common/apiCommons';
import useTranslate from '~/common/utils/translate';
import * as ReactRouter from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import _ from 'lodash';

export const useProject = () => {
  const params = ReactRouter.useParams<{ id: string }>();

  const [{ data: project }] = ProjectApi.hooks.useProjectQuery(
    { id: params?.id },
    {
      enabled: !!params?.id,
    },
  );
  const [{ data: jobs, isLoading: jobsIsLoading }] =
    JobApi.hooks.useJobsOfProjectQuery(project, { enabled: !!project });

  return { project, jobs: _.orderBy(jobs, 'step_number'), jobsIsLoading };
};

export const useCopyJobToProject = () => {
  const navigate = ReactRouter.useNavigate();
  const [copyOpen, setCopyOpen] = React.useState(false);
  const { mutateAsync: copyJobToJobMutation } =
    JobApi.hooks.useDuplicateJobToProjectMutation();
  const copyJob = useCallback((r: { new_project_id: ID; job_id: ID }) => {
    return copyJobToJobMutation(r).then((copiedJob) =>
      navigate(
        routes.EDIT_JOB.replace(':id', `${copiedJob?.id as unknown as string}`),
      ),
    );
  }, []);
  return {
    copyOpen,
    setCopyOpen,
    copyJob,
  };
};

export const useDeleteJob = (job: Job) => {
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const { mutateAsync: deleteJobMutation } = JobApi.hooks.useDeleteJobMutation(
    _.first(job.projects),
  );
  const deleteJob = () => {
    return deleteJobMutation(job).then(() => setConfirmOpen(false));
  };
  return {
    confirmOpen,
    setConfirmOpen,
    deleteJob,
  };
};

export const useMergeJobs = () => {
  const [merging, setMerging] = React.useState(false);
  const { mutateAsync: mergeJobsMutation } =
    JobApi.hooks.useMergeJobsMutation();
  const mergeJobs = ({ jobIds }: { jobIds: (string | number)[] }) => {
    return mergeJobsMutation(jobIds.map((id) => ({ id }))).then(() =>
      setMerging(false),
    );
  };

  return { mergeJobs, merging, setMerging };
};

export const useChecklistPdfDownloader = (project: Project | null) => {
  const toast = useToast();
  const translate = useTranslate();
  const { refetch: fetchChecklistPdf, isFetching: isChecklistPdfLoading } =
    useGetChecklistPdf({
      request: { project: project?.id ?? 0 },
      enabled: false,
    });

  const handleGetChecklist = async (): Promise<void> => {
    try {
      const { data } = await fetchChecklistPdf();
      if (data) {
        const blob = new Blob([data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download =
          (project?.name?.replace(/\s+/g, '') || '') +
          translate('project.jobs.pdf.checklist.filename')!;
        a.click();
        window.URL.revokeObjectURL(url);
        toast({
          title: translate('project.jobs.pdf.checklist.successToast.title')!,
          description: translate('project.jobs.pdf.checklist.successToast')!,
          status: 'success',
          duration: 3000,
          position: 'top',
          variant: 'solid',
        });
      }
    } catch (error) {
      toast({
        title: translate('project.jobs.pdf.checklist.errorToast.title')!,
        description: translate('project.jobs.pdf.checklist.errorToast')!,
        status: 'error',
        duration: 5000,
        position: 'top',
        variant: 'solid',
      });
    }
  };

  return { handleGetChecklist, isChecklistPdfLoading };
};
